import React, { useState, useEffect, useRef } from "react";
import "./CyberneticCommand.scss";

/**
 * CYBERNETIC COMMAND - A cockpit that feels ALIVE
 * Depth, atmosphere, and personality - not just another dashboard
 */
const CyberneticCommand: React.FC = () => {
    const [systemStatus, setSystemStatus] = useState("OPTIMAL");
    const [scanlinePosition, setScanlinePosition] = useState(0);
    const [glitchActive, setGlitchActive] = useState(false);
    const matrixRef = useRef<HTMLCanvasElement>(null);
    const [selectedMode, setSelectedMode] = useState<"hunt" | "defend" | "explore">("hunt");

    // Matrix rain effect
    useEffect(() => {
        const canvas = matrixRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        canvas.width = window.innerWidth;
        canvas.height = window.innerHeight;

        const matrix = "DEVOURPLAY10101XPFUEL";
        const matrixArray = matrix.split("");
        const fontSize = 10;
        const columns = canvas.width / fontSize;
        const drops: number[] = [];

        for (let x = 0; x < columns; x++) {
            drops[x] = 1;
        }

        const draw = () => {
            ctx.fillStyle = "rgba(0, 0, 0, 0.04)";
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            ctx.fillStyle = "#7859E6";
            ctx.font = `${fontSize}px monospace`;

            for (let i = 0; i < drops.length; i++) {
                const text = matrixArray[Math.floor(Math.random() * matrixArray.length)];
                ctx.fillText(text, i * fontSize, drops[i] * fontSize);

                if (drops[i] * fontSize > canvas.height && Math.random() > 0.975) {
                    drops[i] = 0;
                }
                drops[i]++;
            }
        };

        const interval = setInterval(draw, 35);
        return () => clearInterval(interval);
    }, []);

    // Scanline animation
    useEffect(() => {
        const interval = setInterval(() => {
            setScanlinePosition(prev => (prev + 1) % 100);
        }, 50);
        return () => clearInterval(interval);
    }, []);

    // Random glitch effect
    useEffect(() => {
        const glitchInterval = setInterval(() => {
            setGlitchActive(true);
            setTimeout(() => setGlitchActive(false), 100);
        }, 8000);
        return () => clearInterval(glitchInterval);
    }, []);

    return (
        <div className={`cybernetic-command ${glitchActive ? "glitch-active" : ""}`}>
            <canvas ref={matrixRef} className="matrix-rain" />

            {/* HOLOGRAPHIC SCANLINE */}
            <div className="scanline" style={{ top: `${scanlinePosition}%` }} />

            {/* ATMOSPHERIC OVERLAYS */}
            <div className="atmospheric-fog" />
            <div className="vignette-shadow" />

            {/* TOP BAR: SYSTEM STATUS */}
            <div className="system-header">
                <div className="system-id">
                    <div className="id-block">
                        <span className="id-label">PILOT ID</span>
                        <span className="id-value">DK-7749</span>
                    </div>
                    <div className="id-block">
                        <span className="id-label">CLEARANCE</span>
                        <span className="id-value">OMEGA</span>
                    </div>
                </div>

                <div className="system-core">
                    <div className="core-status">
                        <div className="status-rings">
                            <div className="ring ring-1" />
                            <div className="ring ring-2" />
                            <div className="ring ring-3" />
                            <div className="core-dot" />
                        </div>
                        <div className="status-text">
                            SYSTEM: {systemStatus}
                        </div>
                    </div>
                </div>

                <div className="system-metrics">
                    <div className="metric">
                        <span className="metric-label">SYNC</span>
                        <div className="metric-bar">
                            <div className="bar-fill" style={{ width: "87%" }} />
                        </div>
                    </div>
                    <div className="metric">
                        <span className="metric-label">PWR</span>
                        <div className="metric-bar">
                            <div className="bar-fill power" style={{ width: "94%" }} />
                        </div>
                    </div>
                </div>
            </div>

            {/* MAIN VIEWPORT */}
            <div className="viewport-container">

                {/* LEFT: TACTICAL READOUT */}
                <div className="tactical-panel">
                    <div className="panel-frame">
                        <div className="frame-corner top-left" />
                        <div className="frame-corner top-right" />
                        <div className="frame-corner bottom-left" />
                        <div className="frame-corner bottom-right" />

                        <div className="panel-header">
                            <span className="header-text">MISSION TRAJECTORY</span>
                            <div className="header-lights">
                                <span className="light active" />
                                <span className="light active" />
                                <span className="light" />
                            </div>
                        </div>

                        {/* 3D Journey Visualization */}
                        <div className="journey-hologram">
                            <div className="holo-grid">
                                <div className="grid-line horizontal" style={{ top: "20%" }} />
                                <div className="grid-line horizontal" style={{ top: "40%" }} />
                                <div className="grid-line horizontal" style={{ top: "60%" }} />
                                <div className="grid-line horizontal" style={{ top: "80%" }} />
                                <div className="grid-line vertical" style={{ left: "20%" }} />
                                <div className="grid-line vertical" style={{ left: "40%" }} />
                                <div className="grid-line vertical" style={{ left: "60%" }} />
                                <div className="grid-line vertical" style={{ left: "80%" }} />
                            </div>

                            <div className="journey-path">
                                <div className="path-node completed">
                                    <div className="node-core" />
                                    <div className="node-label">ALPHA POINT</div>
                                    <div className="node-energy" />
                                </div>

                                <div className="path-connection active" />

                                <div className="path-node active">
                                    <div className="node-core" />
                                    <div className="node-label">NEXUS BRIDGE</div>
                                    <div className="node-energy" />
                                    <div className="node-scanner" />
                                </div>

                                <div className="path-connection" />

                                <div className="path-node locked">
                                    <div className="node-core" />
                                    <div className="node-label">OMEGA VAULT</div>
                                    <div className="lock-field" />
                                </div>
                            </div>

                            {/* Floating data particles */}
                            <div className="data-particle" style={{ "--delay": "0s" } as any} />
                            <div className="data-particle" style={{ "--delay": "1s" } as any} />
                            <div className="data-particle" style={{ "--delay": "2s" } as any} />
                        </div>

                        {/* Tactical Stats */}
                        <div className="tactical-stats">
                            <div className="stat-row">
                                <span className="stat-icon">◈</span>
                                <span className="stat-name">ELIMINATIONS</span>
                                <span className="stat-value">247</span>
                            </div>
                            <div className="stat-row">
                                <span className="stat-icon">◉</span>
                                <span className="stat-name">ACCURACY</span>
                                <span className="stat-value">78.4%</span>
                            </div>
                            <div className="stat-row">
                                <span className="stat-icon">◆</span>
                                <span className="stat-name">FUEL CELLS</span>
                                <span className="stat-value">3,847</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* CENTER: COMMAND INTERFACE */}
                <div className="command-center">
                    {/* Mode Selector */}
                    <div className="mode-selector">
                        <button
                            className={`mode-tab ${selectedMode === "hunt" ? "active" : ""}`}
                            onClick={() => setSelectedMode("hunt")}
                        >
                            <span className="mode-icon">⟟</span>
                            <span className="mode-name">HUNT</span>
                        </button>
                        <button
                            className={`mode-tab ${selectedMode === "defend" ? "active" : ""}`}
                            onClick={() => setSelectedMode("defend")}
                        >
                            <span className="mode-icon">⬡</span>
                            <span className="mode-name">DEFEND</span>
                        </button>
                        <button
                            className={`mode-tab ${selectedMode === "explore" ? "active" : ""}`}
                            onClick={() => setSelectedMode("explore")}
                        >
                            <span className="mode-icon">⬢</span>
                            <span className="mode-name">EXPLORE</span>
                        </button>
                    </div>

                    {/* Primary Display */}
                    <div className="primary-display">
                        <div className="display-frame">
                            {/* Holographic Quest Card */}
                            <div className="holo-quest-card">
                                <div className="card-scanlines" />
                                <div className="card-static" />

                                <div className="card-header">
                                    <span className="card-type">PRIORITY TARGET</span>
                                    <span className="card-timer">T-04:23:16</span>
                                </div>

                                <div className="card-body">
                                    <h2 className="quest-title">SHADOW PROTOCOL</h2>
                                    <div className="quest-params">
                                        <div className="param">
                                            <span className="param-label">OBJECTIVE</span>
                                            <span className="param-value">Eliminate 15 targets undetected</span>
                                        </div>
                                        <div className="param">
                                            <span className="param-label">PROGRESS</span>
                                            <div className="progress-tracker">
                                                <div className="progress-segments">
                                                    {[...Array(15)].map((_, i) =>
                                                        <div key={i} className={`segment ${i < 9 ? "complete" : ""}`} />)}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="reward-matrix">
                                        <div className="matrix-label">REWARD MATRIX</div>
                                        <div className="rewards">
                                            <div className="reward-cell">
                                                <span className="reward-icon">⚡</span>
                                                <span className="reward-value">2.5K</span>
                                            </div>
                                            <div className="reward-cell premium">
                                                <span className="reward-icon">💎</span>
                                                <span className="reward-value">RARE</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <button className="engage-button">
                                    <span className="button-text">ENGAGE</span>
                                    <div className="button-energy" />
                                </button>
                            </div>

                            {/* Secondary Objectives */}
                            <div className="objective-grid">
                                <div className="objective-cell">
                                    <div className="cell-border" />
                                    <div className="cell-content">
                                        <span className="objective-icon">🎯</span>
                                        <span className="objective-name">HEADHUNTER</span>
                                        <div className="objective-progress">
                                            <div className="progress-fill" style={{ width: "67%" }} />
                                        </div>
                                    </div>
                                </div>
                                <div className="objective-cell">
                                    <div className="cell-border" />
                                    <div className="cell-content">
                                        <span className="objective-icon">⚔️</span>
                                        <span className="objective-name">SQUAD WIPE</span>
                                        <div className="objective-progress">
                                            <div className="progress-fill" style={{ width: "40%" }} />
                                        </div>
                                    </div>
                                </div>
                                <div className="objective-cell urgent">
                                    <div className="cell-border" />
                                    <div className="urgency-pulse" />
                                    <div className="cell-content">
                                        <span className="objective-icon">⏰</span>
                                        <span className="objective-name">FLASH OP</span>
                                        <span className="objective-timer">00:47:32</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Data Stream */}
                    <div className="data-stream">
                        <div className="stream-header">
                            <span className="stream-icon">◉</span>
                            <span className="stream-title">LIVE INTEL</span>
                            <div className="stream-indicator" />
                        </div>
                        <div className="stream-content">
                            <div className="intel-item">
                                <span className="intel-time">00:02</span>
                                <span className="intel-text">Target eliminated: +150 XP</span>
                            </div>
                            <div className="intel-item priority">
                                <span className="intel-time">00:15</span>
                                <span className="intel-text">SQUAD LEADER BONUS: +500 XP</span>
                            </div>
                            <div className="intel-item">
                                <span className="intel-time">00:32</span>
                                <span className="intel-text">Stealth chain x5</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* RIGHT: ARSENAL & INVENTORY */}
                <div className="arsenal-panel">
                    <div className="panel-frame">
                        <div className="frame-corner top-left" />
                        <div className="frame-corner top-right" />
                        <div className="frame-corner bottom-left" />
                        <div className="frame-corner bottom-right" />

                        <div className="panel-header">
                            <span className="header-text">COMBAT ASSETS</span>
                        </div>

                        {/* 3D Inventory Grid */}
                        <div className="inventory-hologram">
                            <div className="inventory-layer layer-1">
                                <div className="item-slot legendary">
                                    <div className="slot-frame" />
                                    <div className="slot-icon">🎁</div>
                                    <div className="slot-rarity">MYTHIC</div>
                                    <div className="slot-glow" />
                                </div>
                                <div className="item-slot epic">
                                    <div className="slot-frame" />
                                    <div className="slot-icon">⚡</div>
                                    <div className="slot-label">2X BOOST</div>
                                </div>
                            </div>
                            <div className="inventory-layer layer-2">
                                <div className="item-slot rare">
                                    <div className="slot-frame" />
                                    <div className="slot-icon">💎</div>
                                    <div className="slot-count">3</div>
                                </div>
                                <div className="item-slot common">
                                    <div className="slot-frame" />
                                    <div className="slot-icon">🎟️</div>
                                    <div className="slot-label">FUEL x100</div>
                                </div>
                            </div>
                        </div>

                        {/* Squad Status */}
                        <div className="squad-status">
                            <div className="status-header">SQUAD NEXUS</div>
                            <div className="squad-members">
                                <div className="member online">
                                    <div className="member-avatar" />
                                    <div className="member-name">SHADOW</div>
                                    <div className="member-status">IN COMBAT</div>
                                </div>
                                <div className="member online">
                                    <div className="member-avatar" />
                                    <div className="member-name">PHOENIX</div>
                                    <div className="member-status">READY</div>
                                </div>
                                <div className="member offline">
                                    <div className="member-avatar" />
                                    <div className="member-name">GHOST</div>
                                    <div className="member-status">OFFLINE</div>
                                </div>
                            </div>
                        </div>

                        {/* Quick Deploy */}
                        <button className="deploy-button">
                            <div className="deploy-frame" />
                            <span className="deploy-text">DEPLOY</span>
                            <div className="deploy-energy" />
                        </button>
                    </div>
                </div>
            </div>

            {/* BOTTOM: SYSTEM READOUTS */}
            <div className="system-footer">
                <div className="readout-section">
                    <span className="readout-label">FUEL RESERVES</span>
                    <span className="readout-value">3,847</span>
                </div>
                <div className="readout-section">
                    <span className="readout-label">COMBAT RATING</span>
                    <span className="readout-value">ELITE</span>
                </div>
                <div className="readout-section">
                    <span className="readout-label">NEXT UNLOCK</span>
                    <span className="readout-value">2,153 XP</span>
                </div>
                <div className="readout-section">
                    <span className="readout-label">UPLINK STATUS</span>
                    <span className="readout-value status-good">STABLE</span>
                </div>
            </div>

            {/* ENVIRONMENTAL EFFECTS */}
            <div className="environment-effects">
                <div className="interference-pattern" />
                <div className="hologram-flicker" />
                <div className="depth-fog" />
            </div>
        </div>
    );
};

export default CyberneticCommand;